import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { SOURCE_COLORS, type FlowRow, type ModifierValue } from '../../utils/economyFlow';

/**
 * SF-164: `FlowSection`/`ModCell`, ported verbatim (JSX-only — no non-component exports, see
 * `economyFlow.ts`'s `ROW_HOVER_STYLE`/`SOURCE_COLORS` for the constants this file used to own)
 * out of the deleted `components/council/tabs/ResourceFlowsTab.tsx`. Consumed by
 * `DesktopEconomyShell` for the three "{Gold|Food|Runes} Details" tables.
 *
 * `ModCell`'s prop type changes from `{ value: string | null }` to the structured
 * `{ value: ModifierValue | null }` (v1.3 fix) — colors from the explicit `negative` boolean, no
 * string-sniffing.
 */
export function ModCell({ value }: { value: ModifierValue | null }) {
  if (!value) {
    return <span className="text-center text-sm font-mono" style={{ color: COLORS.text.tertiary }}>—</span>;
  }
  const color = value.negative ? COLORS.negative : COLORS.positive;
  return <span className="text-center text-sm font-mono" style={{ color }}>{value.text}</span>;
}

export function FlowSection({ title, rows, color }: { title: string; rows: FlowRow[]; color: string }) {
  return (
    <div
      className="rounded overflow-hidden"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
    >
      {/* Header */}
      <div
        className="grid items-center px-3 py-1.5"
        style={{
          gridTemplateColumns: '1fr 180px 100px',
          backgroundColor: COLORS.sectionHeader,
        }}
      >
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>
          {title}
        </span>
        <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>
          Calculation
        </span>
        <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>
          Amount
        </span>
      </div>

      {/* Rows */}
      {rows.map((row, idx) => {
        const isNegative = row.value < 0;
        const valueColor = row.isTotal
          ? (row.value >= 0 ? COLORS.positive : COLORS.negative)
          : row.isSubtotal
            ? COLORS.text.primary
            : COLORS.text.secondary;

        return (
          <div
            key={idx}
            className="flow-row grid items-center px-3 py-1.5"
            style={{
              gridTemplateColumns: '1fr 180px 100px',
              borderBottom: `1px solid ${COLORS.border}`,
              backgroundColor: row.isTotal ? COLORS.sectionHeader : row.isSubtotal ? 'rgba(255,255,255,0.02)' : undefined,
              opacity: row.dimmed ? 0.4 : 1,
              transition: 'background-color 0s',
            }}
          >
            <span
              className={`text-sm ${row.isTotal || row.isSubtotal ? 'font-semibold' : ''}`}
              style={{
                color: row.indent ? SOURCE_COLORS[row.source] : COLORS.text.primary,
                paddingLeft: row.indent ? '16px' : undefined,
              }}
            >
              {row.indent && (
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full mr-1.5"
                  style={{ backgroundColor: SOURCE_COLORS[row.source] }}
                />
              )}
              {row.label}
            </span>
            <span className="text-right text-sm font-mono" style={{ color: COLORS.text.tertiary }}>
              {row.indent ? '' : (row.calculation ?? '')}
            </span>
            <span
              className={`text-right text-sm font-mono ${row.isTotal || row.isSubtotal ? 'font-semibold' : ''}`}
              style={{ color: valueColor }}
            >
              {row.indent
                ? row.unit
                : row.isTotal || row.isSubtotal
                  ? `${formatNumber(Math.round(row.value))} ${row.unit}`
                  : `${isNegative ? '-' : ''}${formatNumber(Math.abs(Math.round(row.value)))} ${row.unit}`
              }
            </span>
          </div>
        );
      })}
    </div>
  );
}
